import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, Globe, Plane, Waves, Sparkles, UtensilsCrossed } from 'lucide-react';
import { experiences } from '../data/mock';
import { Button } from './ui/button';

const iconMap = {
  Compass: Compass,
  Globe: Globe,
  Plane: Plane,
  Waves: Waves,
  Sparkles: Sparkles,
  UtensilsCrossed: UtensilsCrossed,
};

export const ExperiencesSection = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-white" id="experiences">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-serif text-stone-800 mb-4">
            {t('experiences.title')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {t('experiences.subtitle')}
          </p>
        </motion.div>

        {/* Experiences Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {experiences.map((exp, index) => {
            const IconComponent = iconMap[exp.icon] || Compass;
            
            return (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="relative h-full bg-stone-50 rounded-2xl overflow-hidden hover:bg-stone-100 transition-colors duration-300">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={exp.image}
                      alt={exp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent" />
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      <IconComponent className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-serif text-stone-800 mb-3">
                      {exp.title}
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed mb-4">
                      {exp.description}
                    </p>
                    <Button
                      variant="ghost"
                      onClick={() => onNavigate('contact')}
                      className="p-0 h-auto text-amber-700 hover:text-amber-800 hover:bg-transparent group/btn"
                    >
                      {t('experiences.discover')}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
